/**
 * Single source of truth for Lens chart styling constants. Replaces the
 * per-chart `MODEL_COLORS` / `PROJECT_COLORS` arrays (which had duplicate
 * entries — the audit caught colors collide at slots 1 & 5) and the three
 * separate `shortModel()` implementations.
 */

import { CHART_PALETTE } from "../../tokens/charts";

/** Tooltip `contentStyle` used by the inline (Recharts default) tooltip API. */
export const CHART_TOOLTIP_STYLE = {
  background: "var(--card)",
  border: "1px solid var(--border)",
  // radius-ignore: Recharts contentStyle needs a numeric px, not a class; 4 = --radius-xs (on-grid).
  borderRadius: 4,
} as const;

/**
 * Ordered chart palette — the canonical `CHART_PALETTE`, led by indigo
 * (`--chart-1`). Previously this array led with amber and ended in
 * `var(--foreground)` (black), which made the last categorical slice render
 * black. Sourcing from the shared palette keeps every Lens chart in lockstep
 * with the rest of the app and guarantees no black series.
 */
export const CHART_COLORS: string[] = [...CHART_PALETTE];

/**
 * Stable per-model-family color assignments, in palette order so the newest /
 * most-prominent family leads with indigo. Use `colorForModel` rather than
 * reading this directly so unknown models fall back consistently.
 */
export const MODEL_COLOR_BY_FAMILY: Record<string, string> = {
  "opus-4-6":   "var(--chart-1)",
  "sonnet-4-6": "var(--chart-2)",
  "opus-4-5":   "var(--chart-3)",
  "sonnet-4-5": "var(--chart-4)",
  "haiku-4-5":  "var(--chart-5)",
};

export function colorForModel(model: string): string {
  for (const family of Object.keys(MODEL_COLOR_BY_FAMILY)) {
    if (model.includes(family)) return MODEL_COLOR_BY_FAMILY[family]!;
  }
  // Unknown model → a palette hue, never black.
  return "var(--chart-3)";
}

/** Pretty short name. One implementation; consolidates 3 prior copies. */
export function shortModelName(model: string): string {
  if (model.includes("opus-4-6"))   return "Opus 4.6";
  if (model.includes("opus-4-5"))   return "Opus 4.5";
  if (model.includes("sonnet-4-6")) return "Sonnet 4.6";
  if (model.includes("sonnet-4-5")) return "Sonnet 4.5";
  if (model.includes("haiku-4-5"))  return "Haiku 4.5";
  // Generic fallback: trim the date suffix if any
  const parts = model.split("-");
  return parts.slice(0, 3).join("-");
}
